/**
 * Session-level upload receipt for Song Uploader (Option A intake queue).
 * In-memory only — not a durable history surface. Successfully handed-off
 * songs must not re-enter the intake table.
 */

export interface UploadSessionReceipt {
  batchId: string;
  requested: number;
  /** Handed off to Song Processing (not merely Approved). */
  succeeded: number;
  failed: number;
  needsReview: number;
  completedAt: string;
}

export type UploadSessionOutcome = "succeeded" | "failed" | "needsReview";

export function uploadSessionNeedsAttention(
  receipt: UploadSessionReceipt,
): boolean {
  return receipt.failed > 0 || receipt.needsReview > 0;
}

export function buildUploadSessionReceipt(args: {
  batchId: string;
  requested: number;
  succeeded: number;
  failed: number;
  needsReview: number;
  completedAt?: string;
}): UploadSessionReceipt {
  return {
    batchId: args.batchId,
    requested: Math.max(0, args.requested),
    succeeded: Math.max(0, args.succeeded),
    failed: Math.max(0, args.failed),
    needsReview: Math.max(0, args.needsReview),
    completedAt: args.completedAt || new Date().toISOString(),
  };
}

export function formatUploadSessionHeadline(
  receipt: UploadSessionReceipt,
): string {
  return uploadSessionNeedsAttention(receipt)
    ? "Upload complete — attention needed"
    : "Upload complete ✓";
}

export function formatUploadSessionLead(receipt: UploadSessionReceipt): string {
  const n = receipt.succeeded;
  if (n === 0) {
    return uploadSessionNeedsAttention(receipt)
      ? "No songs were handed off to Song Processing."
      : "No songs in this batch.";
  }
  if (n === 1) {
    return "1 song successfully handed off to Song Processing";
  }
  return `${n} songs successfully handed off to Song Processing`;
}

export function formatUploadSessionCounts(receipt: UploadSessionReceipt): string {
  const reviewLabel =
    receipt.needsReview === 1 ? "1 needs review" : `${receipt.needsReview} need review`;
  return `${receipt.succeeded} succeeded · ${receipt.failed} failed · ${reviewLabel}`;
}

export function formatUploadSessionCompletedAt(
  completedAt: string,
  now: Date = new Date(),
): string {
  const d = new Date(completedAt);
  if (Number.isNaN(d.getTime())) return "Completed";
  const label = d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  return sameDay ? `Completed ${label}` : `Completed ${d.toLocaleString()}`;
}

/** Apply an outcome; needsReview wins over succeeded for the same localId. */
export function applyUploadSessionOutcome(
  buckets: {
    succeeded: Set<string>;
    failed: Set<string>;
    needsReview: Set<string>;
  },
  localId: string,
  outcome: UploadSessionOutcome,
): void {
  if (!localId) return;
  buckets.succeeded.delete(localId);
  buckets.failed.delete(localId);
  buckets.needsReview.delete(localId);
  buckets[outcome].add(localId);
}

export function uploadSessionBatchComplete(
  localIds: ReadonlySet<string>,
  buckets: {
    succeeded: ReadonlySet<string>;
    failed: ReadonlySet<string>;
    needsReview: ReadonlySet<string>;
  },
): boolean {
  if (localIds.size === 0) return false;
  for (const id of localIds) {
    if (
      !buckets.succeeded.has(id) &&
      !buckets.failed.has(id) &&
      !buckets.needsReview.has(id)
    ) {
      return false;
    }
  }
  return true;
}
